import Vue from 'vue';
import CRenderer from './renderer/CRenderer';
import { INJECT_UICORE_BUS, INJECT_UICORE_RENDERER } from './utils/consts';
import PortalTarget from './comps/Portal/PortalTarget';
import PortalContext, { INJECT_PORTAL_CONTEXT } from './comps/Portal/PortalContext';
import { getDefaultValue } from '@tencent/data-schema-core';
import '@tencent/data-schema-core/lib/presets/vue';
import { freeDtBus, getDtBus, getDtOption } from './comps/DevTool/util/devToolMixin';
import DevTool from './comps/DevTool';
const props = {
  data: {
    required: false
  },
  dataSchema: {
    type: Object,
    required: false
  },
  dataSources: {
    type: Array,
    default: () => []
  },
  pagelets: {
    type: Array
  },
  pageletId: {
    type: String,
    default: 'default'
  },
  components: {
    type: Array,
    default: () => []
  },
  designerComponents: {
    type: Array,
    default: () => []
  },
  flowAtoms: {
    type: Array,
    default: () => []
  },
  plugins: {
    type: Array,
    default: () => []
  },
  getHighlightDataPath: {
    type: Function,
    required: false
  }
};
export const publicCompMixin = Vue.extend({
  components: {
    CRenderer,
    PortalTarget
  },
  model: {
    prop: 'data'
  },
  props,
  inheritAttrs: false,
  data: () => ({
    // uc: 这里不要加，会出问题
    devTool: null
  }),
  // 注意：public comps 需要自己写一下这个。mixin 的执行顺序有问题
  // beforeCreate() {
  //   this.uc = createBus(this);
  // },
  destroyed() {
    var _this$devTool;
    this.uc.$destroy();
    (_this$devTool = this.devTool) === null || _this$devTool === void 0 || _this$devTool.$destroy();
  },
  computed: {
    rootDataSources() {
      const ans = [...this.dataSources];
      // 如果有传入 data 和 dataSchema，生成 DataSource 吧
      if (this.dataSchema && !ans.some(x => x.id === 'data')) {
        const data = this.data === 'undefined' ? getDefaultValue(this.dataSchema, {
          useLorem: true,
          useExample: true
        }) : this.data;
        ans.push({
          id: 'data',
          schema: {
            ...this.dataSchema,
            title: '数据'
          },
          instance: data
        });
      }
      return ans;
    },
    rootPagelet: {
      get() {
        return this.uc.pageletLUT[this.pageletId];
      },
      set(pagelet) {
        // 一般不会直接改到，但是以防万一还是做一下支持
        const index = this.uc.pagelets.findIndex(pagelet => pagelet.id === this.pageletId);
        if (index === -1) {
          this.uc.pagelets.push(pagelet);
        } else {
          this.$set(this.uc.pagelets, index, pagelet);
        }
        this.uc.notifyPageletsChange(0);
      }
    }
  },
  methods: {
    /**
     * 根据 componentId 获取组件实例
     * @public
     */
    getRef(componentId) {
      var _this$uc$renderer;
      return (_this$uc$renderer = this.uc.renderer) === null || _this$uc$renderer === void 0 ? void 0 : _this$uc$renderer.getRef(componentId);
    },
    /**
     * 根据 path 获取组件实例
     * @public
     */
    getRefByPath(path) {
      var _this$uc$renderer2;
      return (_this$uc$renderer2 = this.uc.renderer) === null || _this$uc$renderer2 === void 0 ? void 0 : _this$uc$renderer2.getRefByPath(path);
    },
    /**
     * 使用 keypath（例如 `"data.friends[0].name"`） 获取某个子 DataStub
     * @public
     */
    getStubAtPath(path) {
      var _this$uc$renderer3;
      return (_this$uc$renderer3 = this.uc.renderer) === null || _this$uc$renderer3 === void 0 ? void 0 : _this$uc$renderer3.getStubAtPath(path);
    },
    /**
     * 打开devTool的方法，调用之后可以从designer和renderer上面获取到devtool实例
     * 使用closeDevtool来关闭弹窗，实例依然可用，如果想删除实例，调用uninstallDevtool
     * 如果切换了模式，devtool也会被清空。
     *
     * 因为dtbus是单例，里面挂载ucBus默认为唤起此dtbus的uc
     *
     * @public
     */
    async openDevtool(opts = {}) {
      var _this$devTool2;
      const dtBus = getDtBus(opts);
      // 开启另外一个dtbus
      if (dtBus.ucBus !== this.uc) {
        dtBus.cleanBus();
        dtBus.options.open = false;
        dtBus.ucBus = this.uc;
      }
      if (dtBus.options.open && dtBus.windowRef) {
        var _dtBus$windowRef;
        if (dtBus.options.displayMode !== 'seperate') {
          dtBus.options.open = false;
          return;
        }
        (_dtBus$windowRef = dtBus.windowRef) === null || _dtBus$windowRef === void 0 || _dtBus$windowRef.focus();
        return;
      }
      dtBus.ucBus = this.uc;
      if (!this.devTool) {
        this.devTool = new DevTool();
      }
      this.uc.hooks.onOpenDevTool.call(this.devTool);
      if (!dtBus.parentEl) {
        const dummy = document.createElement('div');
        dummy.setAttribute('style', 'position:fixed;left:0;top:0;opacity:0;');
        document.body.appendChild(dummy);
        dtBus.parentEl = dummy;
      }
      (_this$devTool2 = this.devTool) === null || _this$devTool2 === void 0 || _this$devTool2.$mount(dtBus.parentEl);
      await this.$nextTick();
      dtBus.options.open = true;
      await this.$nextTick();
      this.uc.hooks.onOpenDevToolDone.call(this.devTool);
    },
    getDtBus(opts) {
      return getDtBus(opts);
    },
    getDtOption() {
      return getDtOption();
    },
    closeDevtool() {
      const dtBus = getDtBus();
      dtBus.$set(dtBus.options, 'open', false);
    },
    uninstallDevTool() {
      if (this.devTool) this.devTool.$destroy();
      getDtBus().cleanBus();
      freeDtBus();
    }
  },
  provide() {
    return {
      [INJECT_UICORE_BUS]: this.uc,
      [INJECT_UICORE_RENDERER]: null,
      [INJECT_PORTAL_CONTEXT]: new PortalContext({
        parent: this
      })
    };
  }
});
//# sourceMappingURL=publicComp.js.map
